// plugins/gtm.js
class GTMManager {
  constructor() {
    this.currentId = null
    this.isLoaded = false
    this.dataLayer = null
    this.scriptElement = null
    this.noScriptElement = null
  }

  /**
   * Charge un nouveau GTM ou change l'ID si différent
   * @param {string} id - L'ID GTM (ex: 'GTM-XXXXXX')
   * @param {Object} config - Configuration optionnelle
   */
  loadGTM(id, config = {}) {
    if (!id || typeof id !== 'string') {
      console.warn('GTM: ID invalide fourni')
      return
    }

    // Si c'est le même ID et qu'il est déjà chargé, ne rien faire
    if (this.currentId === id && this.isLoaded) {
      console.log(`GTM: ${id} déjà chargé`)
      return
    }

    // Supprimer l'ancien GTM si nécessaire
    if (this.isLoaded) {
      this.removeGTM()
    }

    console.log(`GTM: Chargement de ${id}`)

    // Initialiser dataLayer si pas déjà fait
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      this.dataLayer = window.dataLayer
      
      // Push de l'événement de démarrage
      this.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
        ...config.initialData,
      })

      // Charger le script GTM
      this.loadScript(id, config.dataLayerName || 'dataLayer')
      
      // Ajouter le noscript pour les navigateurs sans JS
      this.loadNoScript(id)

      this.currentId = id
      this.isLoaded = true
    }
  }

  /**
   * Charge le script GTM
   * @param {string} id - L'ID GTM
   * @param {string} dataLayerName - Nom du dataLayer
   */
  loadScript(id, dataLayerName = 'dataLayer') {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}&l=${dataLayerName}`
    script.onload = () => {
      console.log(`GTM: Script ${id} chargé avec succès`)
    }
    script.onerror = () => {
      console.error(`GTM: Erreur lors du chargement de ${id}`)
    }
    
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript.parentNode.insertBefore(script, firstScript)
    
    this.scriptElement = script
  }

  /**
   * Ajouter le noscript GTM dans le body
   * @param {string} id - L'ID GTM
   */
  loadNoScript(id) {
    // Supprimer l'ancien noscript s'il existe
    if (this.noScriptElement) {
      this.noScriptElement.remove()
    }

    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    
    noscript.appendChild(iframe)
    document.body.insertBefore(noscript, document.body.firstChild)
    
    this.noScriptElement = noscript
  }

  /**
   * Supprime le GTM actuel
   */
  removeGTM() {
    if (!this.isLoaded) return

    console.log(`GTM: Suppression de ${this.currentId}`)

    // Supprimer le script
    if (this.scriptElement) {
      this.scriptElement.remove()
      this.scriptElement = null
    }

    // Supprimer le noscript
    if (this.noScriptElement) {
      this.noScriptElement.remove()
      this.noScriptElement = null
    }

    // Nettoyer les variables GTM dans window
    if (typeof window !== 'undefined') {
      // Supprimer les fonctions GTM du window
      delete window.google_tag_manager
      delete window.gtag
    }

    this.currentId = null
    this.isLoaded = false
  }

  /**
   * Envoie un événement vers GTM
   * @param {string} event - Nom de l'événement
   * @param {Object} parameters - Paramètres de l'événement
   */
  trackEvent(event, parameters = {}) {
    if (!this.isLoaded) {
      console.warn('GTM: Aucun GTM chargé, impossible d\'envoyer l\'événement')
      return
    }

    if (typeof window !== 'undefined' && window.dataLayer) {
      const eventData = {
        event,
        gtm_id: this.currentId,
        timestamp: new Date().toISOString(),
        ...parameters,
      }
      
      window.dataLayer.push(eventData)
      console.log('GTM: Événement envoyé', eventData)
    }
  }

  /**
   * Envoie des données personnalisées vers GTM
   * @param {Object} data - Données à envoyer
   */
  pushData(data) {
    if (!this.isLoaded) {
      console.warn('GTM: Aucun GTM chargé, impossible d\'envoyer les données')
      return
    }

    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        gtm_id: this.currentId,
        timestamp: new Date().toISOString(),
        ...data,
      })
      console.log('GTM: Données envoyées', data)
    }
  }

  /**
   * Définit des variables personnalisées
   * @param {Object} variables - Variables à définir
   */
  setCustomVariables(variables) {
    this.pushData({
      event: 'customVariables',
      customVariables: variables,
    })
  }

  /**
   * Track une page vue
   * @param {Object} pageData - Données de la page
   */
  trackPageView(pageData = {}) {
    const data = {
      event: 'pageView',
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...pageData,
    }
    
    this.pushData(data)
  }

  /**
   * Obtient l'ID GTM actuel
   * @returns {string|null}
   */
  getCurrentId() {
    return this.currentId
  }

  /**
   * Vérifie si GTM est chargé
   * @returns {boolean}
   */
  isGTMLoaded() {
    return this.isLoaded
  }

  /**
   * Active/désactive le mode debug
   * @param {boolean} enabled
   */
  // setDebugMode(enabled) {
  //   if (typeof window !== 'undefined') {
  //     if (enabled) {
  //       window.dataLayer = window.dataLayer || []
  //       window.dataLayer.push({
  //         event: 'gtm.debug',
  //         debug: true,
  //       })
  //     }
  //   }
  // }
}

// Instance unique du manager
const gtmManager = new GTMManager()

// Plugin Vue
export default {
  install(Vue, options = {}) {
    // Ajouter le manager à Vue prototype
    Vue.prototype.$gtm = gtmManager
    
    // Configuration initiale si fournie
    if (options.id) {
      gtmManager.loadGTM(options.id, options)
    }

    // Mixin global pour faciliter l'usage
    Vue.mixin({
      methods: {
        $trackEvent(event, parameters) {
          return this.$gtm.trackEvent(event, parameters)
        },
        $trackPageView(pageData) {
          return this.$gtm.trackPageView(pageData)
        },
      },
    })

    // Configuration pour le mode développement
    // if (process.env.NODE_ENV === 'development' && options.debug !== false) {
    //   gtmManager.setDebugMode(true)
    // }
  },
}

// Export de la classe pour usage direct si nécessaire
export { GTMManager }
